import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.jsx';
import { Modal } from '../../components/Modal.jsx';

function formatDate(value) {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
}

function AddAdminModal({ tenantId, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.post(`/api/super-admin/tenants/${tenantId}/admins`, {
        email: email.trim(),
        name: name.trim(),
      });
      onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="add-admin-title" busy={submitting}>
      <h2 id="add-admin-title">Add tenant admin</h2>
      <p>We’ll email the restaurant owner a secure link to set up their account.</p>

      {error && <p className="field-error">{error}</p>}

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="admin-name">Name</label>
          <input id="admin-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="admin-email">Email</label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteSentModal({ admin, onDismiss }) {
  return (
    <Modal onClose={onDismiss} labelledBy="invite-sent-title">
      <h2 id="invite-sent-title">Invitation sent</h2>
      <div className="alert alert-success">
        <strong style={{ display: 'block', marginBottom: 4 }}>
          {admin.name} ({admin.email})
        </strong>
        We’ve queued an email with a secure setup link. The link expires in 72 hours.
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={onDismiss}>
          Done
        </button>
      </div>
    </Modal>
  );
}

export function TenantDetailPage() {
  const { tenantId } = useParams();
  const { data: tenant, error, loading, reload } = useApiQuery(
    () => api.get(`/api/super-admin/tenants/${tenantId}`),
    [tenantId],
  );
  const { data: menus, error: menusError, loading: menusLoading } = useApiQuery(
    () => api.get(`/api/super-admin/tenants/${tenantId}/menus`),
    [tenantId],
  );

  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [savingForm, setSavingForm] = useState(false);

  const [statusError, setStatusError] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const [adminsError, setAdminsError] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminWithPassword, setNewAdminWithPassword] = useState(null);
  const [disablingAdmin, setDisablingAdmin] = useState(null);
  const [adminActionId, setAdminActionId] = useState(null);

  useEffect(() => {
    if (tenant && form === null) {
      setForm({
        name: tenant.name,
        slug: tenant.slug,
        contactEmail: tenant.contactEmail || '',
        currency: tenant.currency || '',
        menuLimit: String(tenant.menuLimit ?? 1),
      });
    }
  }, [tenant, form]);

  function updateField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setFormSuccess(false);
  }

  async function handleSaveDetails(e) {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);
    setSavingForm(true);
    try {
      const updated = await api.patch(`/api/super-admin/tenants/${tenantId}`, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        contactEmail: form.contactEmail.trim() || null,
        currency: form.currency.trim() || undefined,
        menuLimit: Number(form.menuLimit),
      });
      setForm({
        name: updated.name,
        slug: updated.slug,
        contactEmail: updated.contactEmail || '',
        currency: updated.currency || '',
        menuLimit: String(updated.menuLimit ?? 1),
      });
      setFormSuccess(true);
      reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSavingForm(false);
    }
  }

  async function handleActivate() {
    setStatusError(null);
    setStatusSubmitting(true);
    try {
      await api.post(`/api/super-admin/tenants/${tenantId}/activate`);
      reload();
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusSubmitting(false);
    }
  }

  async function handleConfirmDeactivate() {
    setStatusError(null);
    setStatusSubmitting(true);
    try {
      await api.post(`/api/super-admin/tenants/${tenantId}/deactivate`);
      reload();
      setShowDeactivateConfirm(false);
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusSubmitting(false);
    }
  }

  async function handleEnableAdmin(admin) {
    setAdminsError(null);
    setAdminActionId(admin.id);
    try {
      await api.post(`/api/super-admin/tenants/${tenantId}/admins/${admin.id}/enable`);
      reload();
    } catch (err) {
      setAdminsError(err.message);
    } finally {
      setAdminActionId(null);
    }
  }

  async function handleConfirmDisableAdmin() {
    const admin = disablingAdmin;
    setAdminsError(null);
    setAdminActionId(admin.id);
    try {
      await api.post(`/api/super-admin/tenants/${tenantId}/admins/${admin.id}/disable`);
      reload();
      setDisablingAdmin(null);
    } catch (err) {
      setAdminsError(err.message);
    } finally {
      setAdminActionId(null);
    }
  }

  if (loading && !tenant) {
    return (
      <div className="page">
        <span className="spinner" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <p>
          <Link to="/super-admin/tenants">&larr; Back to tenants</Link>
        </p>
        <p className="field-error">{error.message}</p>
      </div>
    );
  }

  if (!tenant || !form) return null;

  const admins = tenant.admins ?? [];
  const tenantMenus = menus ?? [];

  return (
    <div className="page tenant-detail-page">
      <Link className="back-link" to="/super-admin/tenants">&larr; Back to tenants</Link>

      <div className="page-header">
        <div>
          <h1>{tenant.name}</h1>
          <p>/{tenant.slug}</p>
        </div>
        <span className={`badge ${tenant.isActive ? 'badge-success' : 'badge-muted'}`}>
          {tenant.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="tenant-detail-grid">
        <div className="panel tenant-details-panel">
          <h2>Details</h2>
          <p className="section-description">Restaurant information and menu access.</p>

          {formError && <p className="field-error">{formError}</p>}
          {formSuccess && <div className="alert alert-success">Tenant updated.</div>}

          <form className="tenant-details-form" onSubmit={handleSaveDetails}>
            <div className="field">
              <label htmlFor="detail-name">Restaurant name</label>
              <input
                id="detail-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="detail-slug">URL slug</label>
              <input
                id="detail-slug"
                type="text"
                required
                pattern="[a-z0-9]+(-[a-z0-9]+)*"
                title="Lowercase letters, numbers, and hyphens only"
                value={form.slug}
                onChange={(e) => updateField('slug', e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="detail-contact-email">Contact email</label>
              <input
                id="detail-contact-email"
                type="email"
                value={form.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="detail-currency">Currency code</label>
              <input
                id="detail-currency"
                type="text"
                maxLength={3}
                pattern="[A-Za-z]{3}"
                title="3-letter currency code, e.g. INR"
                value={form.currency}
                onChange={(e) => updateField('currency', e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="detail-menu-limit">Menu limit</label>
              <input
                id="detail-menu-limit"
                type="number"
                min="1"
                max="100"
                step="1"
                required
                value={form.menuLimit}
                onChange={(e) => updateField('menuLimit', e.target.value)}
              />
              <span className="field-hint">Active menus this tenant can create. New tenants start with 1.</span>
            </div>

            <button type="submit" className="btn btn-primary" disabled={savingForm}>
              {savingForm ? 'Saving…' : 'Save changes'}
            </button>
          </form>

          <div className="tenant-status-section">
            {statusError && <p className="field-error">{statusError}</p>}

            <h3>Status</h3>
            <p>
              {tenant.isActive
                ? 'This tenant is active. Its admins can sign in and its public menu is live.'
                : 'This tenant is inactive. Its admins cannot sign in and its public menu is offline.'}
            </p>
            {tenant.isActive ? (
              <button
                type="button"
                className="btn btn-danger"
                disabled={statusSubmitting}
                onClick={() => setShowDeactivateConfirm(true)}
              >
                Deactivate tenant
              </button>
            ) : (
              <button type="button" className="btn btn-primary" disabled={statusSubmitting} onClick={handleActivate}>
                Activate tenant
              </button>
            )}
          </div>
        </div>

        <div className="panel">
          <h2>Menus</h2>
          <p className="section-description">Preview the menus managed by this restaurant.</p>
          <div className="tenant-menu-capacity" style={{ marginBottom: 16 }}>
            <strong>{tenantMenus.length} of {tenant.menuLimit ?? 1} menus used</strong>
            <span>{tenantMenus.length >= (tenant.menuLimit ?? 1) ? 'The tenant is at its menu limit.' : `${(tenant.menuLimit ?? 1) - tenantMenus.length} menu slot${(tenant.menuLimit ?? 1) - tenantMenus.length === 1 ? '' : 's'} available.`}</span>
          </div>

          {menusError && <p className="field-error">{menusError.message}</p>}

          {menusLoading ? (
            <span className="spinner" aria-label="Loading" />
          ) : tenantMenus.length === 0 ? (
            <div className="empty">
              <strong>No menus yet</strong>
              <p>This restaurant hasn't created a menu.</p>
            </div>
          ) : (
            <div className="list">
              {tenantMenus.map((menu) => (
                <div key={menu.id} className="list-row">
                  <div>
                    <div style={{ fontWeight: 600 }}>{menu.name}</div>
                    <div className="field-hint">
                      /{menu.slug} · {menu._count.categories} {menu._count.categories === 1 ? 'category' : 'categories'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {menu.isPublished ? (
                      <span className="badge badge-success">Published</span>
                    ) : (
                      <span className="badge badge-muted">Draft</span>
                    )}
                    <Link to={`/super-admin/tenants/${tenantId}/menus/${menu.id}`} className="btn btn-sm">
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="page-header" style={{ marginBottom: 12 }}>
            <h2>Tenant admins</h2>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAddAdmin(true)}>
              + Add admin
            </button>
          </div>

          {adminsError && <p className="field-error">{adminsError}</p>}

          {admins.length === 0 ? (
            <div className="empty">
              <strong>No admins yet</strong>
              <p>Add an admin so this restaurant's owner can manage their own menu.</p>
              <button type="button" className="btn btn-primary" onClick={() => setShowAddAdmin(true)}>
                + Add admin
              </button>
            </div>
          ) : (
            <div className="list">
              {admins.map((admin) => (
                <div key={admin.id} className="list-row">
                  <div>
                    <div style={{ fontWeight: 600 }}>{admin.name}</div>
                    <div className="field-hint">{admin.email}</div>
                    <div className="field-hint">Last login: {formatDate(admin.lastLoginAt)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className={`badge ${admin.isActive ? 'badge-success' : 'badge-muted'}`}>
                      {admin.isActive ? 'Active' : 'Disabled'}
                    </span>
                    {admin.isActive ? (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={adminActionId === admin.id}
                        onClick={() => setDisablingAdmin(admin)}
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={adminActionId === admin.id}
                        onClick={() => handleEnableAdmin(admin)}
                      >
                        Enable
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showDeactivateConfirm && (
        <ConfirmDialog
          title="Deactivate this tenant?"
          description="All of this tenant's admins will be logged out immediately and its public menu will go offline."
          confirmLabel="Deactivate"
          danger
          busy={statusSubmitting}
          error={statusError}
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setShowDeactivateConfirm(false)}
        />
      )}

      {disablingAdmin && (
        <ConfirmDialog
          title="Disable this admin?"
          description={`${disablingAdmin.name} (${disablingAdmin.email}) will be logged out immediately and won't be able to sign in until re-enabled.`}
          confirmLabel="Disable"
          danger
          busy={adminActionId === disablingAdmin.id}
          error={adminsError}
          onConfirm={handleConfirmDisableAdmin}
          onCancel={() => setDisablingAdmin(null)}
        />
      )}

      {showAddAdmin && (
        <AddAdminModal
          tenantId={tenantId}
          onClose={() => setShowAddAdmin(false)}
          onCreated={(created) => {
            setShowAddAdmin(false);
            setNewAdminWithPassword(created);
            reload();
          }}
        />
      )}

      {newAdminWithPassword && (
        <InviteSentModal admin={newAdminWithPassword} onDismiss={() => setNewAdminWithPassword(null)} />
      )}
    </div>
  );
}
